'use client';

import { Button, Dialog, Flex, Input, Portal, Text } from '@chakra-ui/react';
import { memo, useCallback, useState } from 'react';

import PhoneInput from '@components/common/PhoneInput';

interface Props {
  open: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  initialName?: string;
}

const inputProps = {
  borderColor: 'border.light',
  borderRadius: 'md',
  size: 'sm' as const,
};

// UI-only: the create flow collects details and closes (no persistence yet).
const NewCustomerDialog = memo(({ open, onOpenChange, initialName = '' }: Props) => {
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState('');
  const [wasOpen, setWasOpen] = useState(open);

  // Reset the form each time the dialog opens (during render, not in an effect).
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setName(initialName);
      setPhone('');
    }
  }

  const onChangeName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value), []);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} placement="center">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="xl">
            <Dialog.Header>
              <Dialog.Title>New customer</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Flex direction="column" gap={3}>
                <Flex direction="column" gap={1}>
                  <Text fontSize="xs" color="text.secondary">
                    Full name
                  </Text>
                  <Input placeholder="Customer name" value={name} onChange={onChangeName} {...inputProps} />
                </Flex>
                <Flex direction="column" gap={1}>
                  <Text fontSize="xs" color="text.secondary">
                    Phone number
                  </Text>
                  <PhoneInput value={phone} onChange={setPhone} size="sm" />
                </Flex>
                <Flex direction="column" gap={1}>
                  <Flex justifyContent="space-between" alignItems="baseline">
                    <Text fontSize="xs" color="text.secondary">
                      Email
                    </Text>
                    <Text fontSize="2xs" color="text.secondary">
                      Optional
                    </Text>
                  </Flex>
                  <Input type="email" placeholder="name@example.com" {...inputProps} />
                </Flex>
              </Flex>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline" size="xs" borderRadius="md">
                  Cancel
                </Button>
              </Dialog.ActionTrigger>
              <Dialog.ActionTrigger asChild>
                <Button size="xs" borderRadius="md">
                  Add customer
                </Button>
              </Dialog.ActionTrigger>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
});
NewCustomerDialog.displayName = 'NewCustomerDialog';

export default NewCustomerDialog;
